import { keyframes } from "@emotion/react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import PostAddIcon from "@mui/icons-material/PostAdd";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useParams } from "react-router";

import { TextLlm } from "Infrastructure/Api/Api";
import { Radius } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useGenerateVoicelinesMutation } from "Projects/Api/Mutations/useGenerateVoicelinesMutation";
import { useProjectActorVoicesQuery } from "Projects/Api/Queries/useProjectActorVoicesQuery";
import { LlmModelSelect } from "Projects/Components/LlmModelSelect";
import {
  createVoiceSegment,
  isMediaSegment,
  isPauseSegment,
  isVoiceSegment,
  useScriptEditor,
} from "Projects/Components/ScriptEditor/ScriptEditorContext";
import { flattenSpans } from "Projects/Utils/spanUtils";
import { splitIntoParagraphs } from "Projects/Utils/splitIntoParagraphs";

const dotBounce = keyframes`
  0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
`;

const LoadingDots = (): React.ReactNode => (
  <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: "primary.main",
          animation: `${dotBounce} 1.4s ease-in-out infinite`,
          animationDelay: `${i * 0.22}s`,
        }}
      />
    ))}
  </Box>
);

const FLOATING_INPUT_HEIGHT = 110;

interface AiRefinementPanelProps {
  /** When provided, a "Back to AI draft" button is rendered next to the title. */
  onBackToDraft?: () => void;
}

export const AiRefinementPanel = ({
  onBackToDraft,
}: AiRefinementPanelProps = {}): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();
  const { projectId, jobId } = useParams<{
    projectId: string;
    jobId: string;
  }>();

  const { script, addSegment, uiDefaults } = useScriptEditor();
  const { data: actorVoices } = useProjectActorVoicesQuery(projectId!);
  const generateMutation = useGenerateVoicelinesMutation(projectId!, jobId!);

  // O(1) lookup map — avoids repeated .find() calls inside buildScriptContext
  const actorVoiceMap = useMemo(
    () => new Map((actorVoices ?? []).map((v) => [v.actorVoiceID, v])),
    [actorVoices],
  );

  const [artifact, setArtifact] = useState("");
  const [instructions, setInstructions] = useState("");
  const [selectedModel, setSelectedModel] = useState<TextLlm>(
    TextLlm.OpenAI_ChatGpt5,
  );

  const buildScriptContext = (): string => {
    return script.segments
      .map((seg, i) => {
        if (isVoiceSegment(seg)) {
          const voice = actorVoiceMap.get(seg.actorVoiceID);
          const label = voice
            ? voice.actorName
            : t(Resources.JobDetail.VoiceSegmentHeading);
          return `[${i + 1}] ${label}: ${flattenSpans(seg.spans)}`;
        }
        if (isPauseSegment(seg)) {
          return `[${i + 1}] ${t(Resources.JobDetail.PauseSegmentHeading)} ${seg.durationMs / 1000}${t(Resources.JobDetail.Seconds)}`;
        }
        if (isMediaSegment(seg)) {
          return `[${i + 1}] ${t(Resources.JobDetail.BackgroundMusic)}`;
        }
        return "";
      })
      .join("\n");
  };

  const handleGenerate = async () => {
    if (!instructions.trim() || generateMutation.isPending) return;
    try {
      const context = buildScriptContext();
      const result = await generateMutation.mutateAsync({
        textLlm: selectedModel,
        instructions,
        context: context || undefined,
      });
      setArtifact(result.generatedContent);
      setInstructions("");
    } catch {
      // error handled by mutation
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleGenerate();
    }
  };

  const handleAddToScript = () => {
    if (!artifact.trim()) return;
    const paragraphs = splitIntoParagraphs(artifact);
    for (const p of paragraphs) {
      addSegment(
        createVoiceSegment({
          actorVoiceID: uiDefaults.defaultActorVoiceID ?? "",
          languageOverride: uiDefaults.defaultLanguage,
          speedFactor: uiDefaults.defaultSpeedFactor,
          volumePercent: uiDefaults.defaultVolumePercent,
          spans: [{ text: p, style: null }],
        }),
      );
    }
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body1" fontSize={15} fontWeight={600}>
          {t(Resources.JobDetail.AiRefinementTitle)}
        </Typography>
        {onBackToDraft && (
          <Button
            variant="text"
            color="primary"
            size="small"
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            onClick={onBackToDraft}
            sx={{ textTransform: "none", fontSize: 12 }}
          >
            {t(Resources.JobDetail.BackToAiDraft)}
          </Button>
        )}
      </Stack>

      <Box
        sx={(th) => ({
          position: "relative",
          border: `1px solid ${th.colors.border}`,
          borderRadius: Radius.M,
          overflow: "hidden",
          minHeight: "220px",
          display: "flex",
          flexDirection: "column",
          backgroundColor: th.colors.backgroundInput,
        })}
      >
        {/* ── Response area ─────────────────────────────────────── */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2,
            pt: 2,
            pb: `${FLOATING_INPUT_HEIGHT + 16}px`,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {generateMutation.isPending ? (
            <Box sx={{ display: "flex", alignItems: "center", pt: 1 }}>
              <LoadingDots />
            </Box>
          ) : artifact ? (
            <>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.75,
                  fontSize: "13px",
                  flex: 1,
                }}
              >
                {artifact}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<PostAddIcon sx={{ fontSize: 14 }} />}
                  onClick={handleAddToScript}
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    fontWeight: 600,
                    borderRadius: Radius.M,
                  }}
                >
                  {t(Resources.JobDetail.AddToScript)}
                </Button>
              </Box>
            </>
          ) : (
            <Typography
              color="text.disabled"
              fontSize="13px"
              sx={{ lineHeight: 1.6 }}
            >
              {t(Resources.JobDetail.AiArtifactEmptyState)}
            </Typography>
          )}
        </Box>

        <Box
          sx={(th) => ({
            position: "absolute",
            bottom: 10,
            left: 10,
            right: 10,
            borderRadius: Radius.M,
            border: `1px solid ${th.colors.border}`,
            backgroundColor: th.palette.background.default,
            boxShadow: "0 4px 20px rgba(0,0,0,0.35)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            transition: "border-color 0.15s",
            "&:focus-within": {
              borderColor: th.palette.primary.main,
            },
          })}
        >
          {/* Textarea */}
          <Box
            component="textarea"
            value={instructions}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setInstructions(e.target.value)
            }
            onKeyDown={handleKeyDown}
            placeholder={t(Resources.JobDetail.InstructionsPlaceholder)}
            disabled={generateMutation.isPending}
            rows={2}
            sx={{
              resize: "none",
              border: "none",
              outline: "none",
              backgroundColor: "transparent",
              color: theme.palette.text.primary,
              fontSize: "13px",
              fontFamily: "inherit",
              lineHeight: 1.6,
              px: 1.75,
              pt: 1.25,
              pb: 0.5,
              width: "100%",
              boxSizing: "border-box",
              "&::placeholder": { color: theme.palette.text.disabled },
              "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
            }}
          />

          {/* Bottom bar: model selector ← → send button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.25,
              pb: 1,
            }}
          >
            <LlmModelSelect value={selectedModel} onChange={setSelectedModel} />

            <IconButton
              onClick={handleGenerate}
              disabled={!instructions.trim() || generateMutation.isPending}
              size="small"
              color="primary"
              sx={(th) => ({
                backgroundColor: th.palette.primary.main,
                color: th.palette.common.white,
                borderRadius: "50%",
                p: 1.5,
                "&:hover": { backgroundColor: th.palette.primary.dark },
                "&.Mui-disabled": {
                  backgroundColor: th.colors.border,
                  color: th.palette.text.disabled,
                },
              })}
            >
              <ArrowUpwardIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
};
